import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';
import { ExportService, ExportJob } from './export.service';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
    selector: 'app-export-progress',
    standalone: true,
    imports: [CommonModule, MatProgressBarModule, MatIconModule, MatButtonModule],
    animations: [
        trigger('slideIn', [
            transition(':enter', [
                style({ transform: 'translateX(100%)', opacity: 0 }),
                animate('200ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 }))
            ])
        ])
    ],
    template: `
        <div
            *ngIf="job"
            @slideIn
            class="fixed bottom-4 right-4 z-50 w-80 bg-card shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">

            <!-- Header -->
            <div class="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center gap-2">
                    <mat-icon
                        *ngIf="job.status === 'queued' || job.status === 'active'"
                        class="icon-size-5 text-blue-500 animate-spin">
                        autorenew
                    </mat-icon>
                    <mat-icon
                        *ngIf="job.status === 'completed'"
                        class="icon-size-5 text-green-500"
                        [svgIcon]="'heroicons_solid:check-circle'">
                    </mat-icon>
                    <mat-icon
                        *ngIf="job.status === 'failed'"
                        class="icon-size-5 text-red-500"
                        [svgIcon]="'heroicons_solid:x-circle'">
                    </mat-icon>
                    <span class="font-semibold text-sm text-gray-900 dark:text-gray-100">
                        {{ getTitle() }}
                    </span>
                </div>
                <button
                    mat-icon-button
                    class="w-6 h-6 min-w-6"
                    (click)="dismiss()"
                    *ngIf="job.status === 'completed' || job.status === 'failed'">
                    <mat-icon class="icon-size-4" [svgIcon]="'heroicons_solid:x-mark'"></mat-icon>
                </button>
            </div>

            <!-- Content -->
            <div class="px-4 py-3">
                <!-- Step message with icon -->
                <div class="flex items-center gap-2 mb-3" *ngIf="job.status === 'queued' || job.status === 'active'">
                    <mat-icon class="icon-size-4 text-blue-500" [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
                    <p class="text-sm text-gray-700 dark:text-gray-300 font-medium">
                        {{ job.message }}
                    </p>
                </div>

                <!-- Success message -->
                <p *ngIf="job.status === 'completed'" class="text-sm text-green-600 dark:text-green-400 mb-2">
                    {{ job.message }}
                </p>

                <!-- Error message -->
                <p *ngIf="job.status === 'failed'" class="text-sm text-red-600 dark:text-red-400 mb-2">
                    {{ job.message }}
                </p>

                <!-- Progress bar for active exports -->
                <div *ngIf="job.status === 'queued' || job.status === 'active'" class="flex items-center gap-2">
                    <mat-progress-bar
                        [mode]="job.progress > 0 ? 'determinate' : 'indeterminate'"
                        [value]="job.progress"
                        class="flex-1 rounded-full"
                        [color]="'primary'">
                    </mat-progress-bar>
                    <span class="text-xs font-semibold text-blue-600 dark:text-blue-400 min-w-10 text-right">
                        {{ job.progress }}%
                    </span>
                </div>

                <!-- Result info for completed exports -->
                <div *ngIf="job.status === 'completed' && job.result" class="text-xs text-gray-500">
                    {{ job.result.recordCount }} records ({{ job.result.fileSizeMB }} MB)
                </div>

                <!-- Error for failed exports -->
                <div *ngIf="job.status === 'failed' && job.error" class="text-xs text-red-500">
                    {{ job.error }}
                </div>
            </div>
        </div>
    `
})
export class ExportProgressComponent implements OnInit, OnDestroy {
    job: ExportJob | null = null;
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private _exportService: ExportService,
        private _cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this._exportService.currentJob$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(job => {
                console.log('ExportProgressComponent received job update:', job);
                this.job = job;
                this._cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    getTitle(): string {
        if (!this.job) return '';

        const typeLabel = this.getTypeLabel();

        switch (this.job.status) {
            case 'queued':
                return `${typeLabel} - Queued`;
            case 'active':
                return `${typeLabel} - In Progress`;
            case 'completed':
                return `${typeLabel} - Complete`;
            case 'failed':
                return `${typeLabel} - Failed`;
            default:
                return typeLabel;
        }
    }

    getTypeLabel(): string {
        if (!this.job) return 'Export';

        switch (this.job.type) {
            case 'orders':
                return 'Orders Export';
            default:
                return 'Export';
        }
    }

    dismiss(): void {
        this._exportService.dismissJob();
    }
}
